import type {Component, Editor} from 'grapesjs';
import {isUserLoggedIn} from '@Libraries/user.library';
import {getPMWMergeTagRegex} from '@Libraries/merge-tags-library';
import {lowerCaseFirstLetter} from '@Utils/string.util';
import {addHttpsToUrl, getUrlHasHttps} from '@Utils/url.util';
import {populateTagsForComponent, removeAllTagsForComponent} from '@Components/merge-tags/merge-tags-reducer';
import {updateSocialLinkSuggestions} from '@Components/email-maker-social-link-suggestions-dropdown/email-maker-social-link-suggestions-dropdown-reducer';
import type {SocialLinkSuggestions} from '@Components/email-maker-social-link-suggestions-dropdown/email-maker-social-link-suggestions-dropdown.types';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import type {UserSignupFormLinkData, UserSignupFormsData} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

/** Social links, signup forms and merge tags for the React email maker. */

/** Normalizes a social profile URL: encodeURI/decodeURI, then prepend `https://` when no scheme is present. */
export function getSanitizedSocialUrl(url: string): string {
  const sanitized = encodeURI(decodeURI(url));
  if (sanitized === '') {
    return sanitized;
  }
  if (getUrlHasHttps(sanitized)) {
    return sanitized;
  }
  return addHttpsToUrl(sanitized);
}

/** Asset path segment that separates the base URL from the style directory: `.../social-icons/<style>/<file>`. */
const SOCIAL_ICONS_DIRECTORY_NAME = '/social-icons/';
/** The icon-style PMW custom style property written onto the socials component (serialized). */
const SOCIAL_ICON_STYLE_PROPERTY = 'icon-style';

const SIGNUP_FORMS_PAGE_SIZE = 30;

let signupFormsData: UserSignupFormsData = {offset: 0, limit: SIGNUP_FORMS_PAGE_SIZE, signupForms: []};

/** Sets the horizontal gap between the selected socials component's icons. */
export function updateSocialLinkSpacing(editor: Editor, gap: number): void {
  const selected = editor.getSelected();

  if (!selected?.is(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS)) {
    return;
  }

  selected.components().each((socialLink) => {
    const style = socialLink.getStyle();
    style['margin-left'] = `${gap}px`;
    style['margin-right'] = `${gap}px`;
    socialLink.setStyle(style);
  });
}

/** Sets the icon size across the selected socials component. */
export function updateSocialLinkSize(editor: Editor, size: number): void {
  const selected = editor.getSelected();

  if (!selected?.is(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS)) {
    return;
  }

  selected.components().each((socialLink) => {
    socialLink.components().each((socialImage) => {
      const style = socialImage.getStyle();
      style.width = `${size}px`;
      style.height = `${size}px`;
      socialImage.setStyle(style);
    });
  });
}

/** Records the icon style on the socials component and rewrites every icon src to the new style directory. */
export function updateSocialLinkIconStyle(editor: Editor, iconStyle: string): void {
  const selected = editor.getSelected();

  setComponentStyle(editor, {
    component: selected,
    property: SOCIAL_ICON_STYLE_PROPERTY,
    value: iconStyle,
  });

  if (!selected?.is(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS)) {
    return;
  }

  selected.components().each((socialLink) => {
    socialLink.components().each((socialImage) => {
      updateSocialImageSrc(socialImage, iconStyle);
    });
  });
}

/** Prefetches the logged-in user's social link suggestions into Redux. */
export async function fetchSocialLinkSuggestions(): Promise<void> {
  if (!isUserLoggedIn()) {
    return;
  }

  try {
    const socialLinkSuggestions = (await window.PMW.writeLocal('emailmaker/getSocialLinksSuggestions', {})) as SocialLinkSuggestions;
    window.PMW.redux.store.dispatch(updateSocialLinkSuggestions(socialLinkSuggestions));
  } catch (error) {
    logEmailMakerError('fetchSocialLinkSuggestions: failed', error);
  }
}

/** Pages through the user's published signup forms into the module cache; the callback receives the index the new page starts at. */
export async function fetchSignupForms(callback?: (newItemsStartIndex: number) => void): Promise<void> {
  if (!isUserLoggedIn()) {
    return;
  }

  const {limit, offset, signupForms} = signupFormsData;

  const data = (await window.PMW.readLocal('formmaker/getPublishedEmailSignupFormLinkDataByUser', {
    limit,
    offset,
  })) as {signupForms: UserSignupFormLinkData[]};

  signupFormsData = {
    ...signupFormsData,
    offset: offset + data.signupForms.length,
    signupForms: [...signupForms, ...data.signupForms],
  };

  if (callback) {
    callback(offset);
  }
}

/** Replaces reads of the legacy SIGNUP_FORMS_DATA global (M4 link-wrapper control). */
export function getSignupFormsData(): UserSignupFormsData {
  return signupFormsData;
}

/** Re-hydrates the merge-tags Redux slice from a text component's content. */
export function updateMergeTags(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();

  if (!target) {
    return;
  }

  if (!target.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT)) {
    return;
  }

  hydrateMergeTagsReduxFromComponent(target);
}

/** Parses [[MergeTag::default]] occurrences out of a text component into Redux. */
export function hydrateMergeTagsReduxFromComponent(component: Component): void {
  if (!component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT)) {
    return;
  }

  const content = component.getEl()?.innerText;

  if (!content?.trim()) {
    window.PMW.redux.store.dispatch(removeAllTagsForComponent({ccid: component.ccid}));
    return;
  }

  const mergeTagPattern = getPMWMergeTagRegex();
  let match;
  const mergeTags = new Set<string>();
  let defaultValueHashMap = {};

  while ((match = mergeTagPattern.exec(content)) !== null) {
    if (match.length <= 1) {
      continue;
    }

    const mergeTagKey = lowerCaseFirstLetter(match[1]);

    if (match.length > 2) {
      const defaultValue = match[2];
      defaultValueHashMap = {
        ...defaultValueHashMap,
        ...(defaultValue && {[mergeTagKey]: defaultValue}),
      };
    }

    mergeTags.add(mergeTagKey);
  }

  if (mergeTags.size > 0) {
    window.PMW.redux.store.dispatch(
      populateTagsForComponent({
        ccid: component.ccid,
        mergeTagKeys: Array.from(mergeTags),
        defaultValueHashmap: defaultValueHashMap,
      })
    );
  }
}

/** Swaps the style directory in the src and data-logo-url attributes of one social icon. */
function updateSocialImageSrc(socialImage: Component, iconStyleDirectory: string): void {
  if (socialImage.get('tagName') !== 'img') {
    return;
  }

  const currentImgSrc = getStringProp(socialImage, 'src') ?? '';
  if (!currentImgSrc) {
    return;
  }

  const updatedImgSrc = getUpdatedSocialImageSrcUrl(currentImgSrc, iconStyleDirectory);
  socialImage.setAttributes({
    ...socialImage.getAttributes(),
    'data-logo-url': updatedImgSrc,
    src: updatedImgSrc,
  });
}

/** Rewrites .../social-icons/&lt;old-style&gt;/&lt;file&gt; to the new style directory. */
function getUpdatedSocialImageSrcUrl(currentImgUrl: string, iconStyleDirectory: string): string {
  if (!currentImgUrl.includes(SOCIAL_ICONS_DIRECTORY_NAME)) {
    return currentImgUrl;
  }

  const [baseUrl, styleSpecificImagePath] = currentImgUrl.split(SOCIAL_ICONS_DIRECTORY_NAME);
  const [, ...imageFileName] = styleSpecificImagePath.split('/');
  const updatedImagePath = [iconStyleDirectory, ...imageFileName].join('/');

  return `${baseUrl}${SOCIAL_ICONS_DIRECTORY_NAME}${updatedImagePath}`;
}
